/**
 * When a guard refuses a call, offer to ask for exactly the capability it named.
 *
 * Both guards put the capability in the 403 body:
 *   require_capability        → "Missing capability 'deployment.read'. …"
 *   require_module_permission → "Access denied — you need 'finance.view' permission. …"
 *   the older middleware      → "Permission denied: finance.view required"
 *
 * One interceptor covers the whole app: every refusal raises a toast with a
 * "Request access" action that files the request for a super admin to approve.
 */
import axios from 'axios';
import { toast } from 'sonner';
import api from '@/lib/api';
import { getToken } from '@/lib/authToken';

const API = process.env.REACT_APP_BACKEND_URL;

const PATTERNS = [
  /Missing capability '([^']+)'/,
  /you need '([^']+)'/,
  /Permission denied: ([\w.]+) required/,
  /You need the ([\w.]+) permission/,
];

export const capabilityFromDetail = (detail) => {
  if (typeof detail !== 'string') return null;
  for (const re of PATTERNS) {
    const m = detail.match(re);
    if (m) return m[1].replace(/\.$/, '');
  }
  return null;
};

const asked = new Set();

const fileRequest = async (capability, path, method) => {
  try {
    await axios.post(
      `${API}/api/admin/rbac/grant-requests`,
      { capability, path, method },
      { headers: { Authorization: `Bearer ${getToken()}`, 'Content-Type': 'application/json' } },
    );
    asked.add(capability);
    toast.success('Access requested', {
      description: `A super admin will see your request for ${capability}.`,
    });
  } catch (e) {
    toast.error(e?.response?.data?.detail || 'Could not file the request');
  }
};

const offer = (error) => {
  const status = error?.response?.status;
  if (status !== 403 || !getToken()) return;
  const capability = capabilityFromDetail(error?.response?.data?.detail);
  if (!capability) return;
  const key = `${capability}`;
  if (asked.has(key)) return;
  asked.add(key);                       // one prompt per capability per session
  const url = error?.config?.url || '';
  toast.error(`You do not have ${capability}`, {
    id: `denied-${capability}`,
    duration: 10000,
    description: 'Ask a super admin for exactly this permission.',
    action: {
      label: 'Request access',
      onClick: () => fileRequest(capability, url, (error?.config?.method || 'get').toUpperCase()),
    },
  });
};

let installed = false;

export const installAccessRequestPrompt = () => {
  if (installed) return;
  installed = true;
  const handler = (error) => { try { offer(error); } catch (_) { /* never block */ } return Promise.reject(error); };
  api.interceptors.response.use((r) => r, handler);
  axios.interceptors.response.use((r) => r, handler);
};

export default installAccessRequestPrompt;
